import { createContext, useCallback, useRef, useState } from "react"
import type { ReactNode } from "react"

// True when the consuming column is the ONLY visible column — i.e. either the
// layout collapsed to it on a narrow viewport, or it was explicitly focused
// (`?focus=n`). Either way it already fills the Main area, so a "fullscreen"
// affordance (e.g. TitleBar's expand button) is redundant and should hide.
// Defaults to false so TitleBars rendered OUTSIDE a ColumnLayout are unaffected.
export const ColumnSoloContext = createContext(false)

// Route-depth → Main columns. A surface declares an ordered list of active
// columns (one per active route depth, shallow→deep); this measures the Main
// container and decides how many render side-by-side, collapsing to the DEEPEST
// active column when narrow (that's the one the user navigated to). Replaces the
// per-surface useReadingPane / hand-rolled ResizeObservers.
//
// SSR-safe: width seeds at 0 → renders the deepest column only (server can't
// measure); the client widens after mount. `focus` forces single-column.

export type ColumnSpec = {
  id: string
  /** px floor to show this column side-by-side with the others. */
  minWidth: number
  /** Flex-fill vs fixed side panel. The deepest column, when alone, always fills. */
  fill?: boolean
  /** Fixed width (px) when not filling. Defaults to minWidth. */
  width?: number
  node: ReactNode
}

export type ColumnLayoutResult = {
  containerRef: (el: HTMLElement | null) => void
  width: number
  /** The subset to render side-by-side, shallow→deep. */
  visible: ColumnSpec[]
  /** True when not every column fits (drives "back" affordances on the deepest). */
  collapsed: boolean
}

export function useColumnLayout(
  columns: ColumnSpec[],
  // `focus` is a 1-based COLUMN number — render only that column full-width
  // (e.g. focus=2 = "fullscreen the 2nd active column"). Out-of-range → ignored.
  opts?: { focus?: number },
): ColumnLayoutResult {
  const [width, setWidth] = useState(0)
  const roRef = useRef<ResizeObserver | null>(null)
  const containerRef = useCallback((el: HTMLElement | null) => {
    roRef.current?.disconnect()
    roRef.current = null
    if (!el) return
    const measure = () => setWidth(el.clientWidth)
    measure()
    const ro = new ResizeObserver(measure)
    ro.observe(el)
    roRef.current = ro
  }, [])

  const focus = opts?.focus
  let visible: ColumnSpec[]
  if (columns.length <= 1) {
    visible = columns
  } else if (focus != null && focus >= 1 && focus <= columns.length) {
    // Explicit fullscreen of a specific column.
    visible = [columns[focus - 1]]
  } else if (width === 0) {
    // SSR / pre-measure → the deepest active column only.
    visible = columns.slice(-1)
  } else {
    // Greedy from the deepest outward — the deepest is always shown; add each
    // shallower column while the running min-width sum still fits.
    const chosen: ColumnSpec[] = []
    let sum = 0
    for (let i = columns.length - 1; i >= 0; i--) {
      const c = columns[i]
      if (chosen.length === 0 || sum + c.minWidth <= width) {
        chosen.unshift(c)
        sum += c.minWidth
      } else break
    }
    visible = chosen
  }

  return { containerRef, width, visible, collapsed: visible.length < columns.length }
}
